import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, Building2, Users, Briefcase, Quote } from 'lucide-react';
export function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError(false);
    const formEl = e.currentTarget;
    try {
      const res = await fetch('https://formspree.io/f/xnjywlka', {
        method: 'POST',
        body: new FormData(formEl),
        headers: {
          Accept: 'application/json'
        }
      });
      if (res.ok) {
        setSubmitted(true);
        formEl.reset();
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="bg-brand-paper" data-id="element-544" data-mp="15g8aqxli8yos">
      {/* Hero with warm background imagery */}
      <section className="relative pt-40 pb-24 overflow-hidden" data-id="element-545" data-mp="19gigqa9arp3n">
        {/* Background image */}
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1542884748-2b87b36c6b90?ixlib=rb-4.0.3&auto=format&fit=crop&w=2800&q=90)'
      }} data-id="element-546" data-mp="1e78pzykugc3m"></div>
        {/* Overlays for legibility */}
        <div className="absolute inset-0 bg-brand-navy/90 mix-blend-multiply" data-id="element-547" data-mp="qj7ld0qsdfth"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/85 to-brand-navy/60" data-id="element-548" data-mp="vgpvuvkgc33l"></div>
        <div className="absolute inset-0 opacity-10" data-id="element-549" data-mp="10hdjr8ddw3tl">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-550" data-mp="26wejlp5j565g"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-551" data-mp="1m6c27dtav10a">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-552" data-mp="1h8sdtl2w17um">
            <div className="inline-flex items-center space-x-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-6" data-id="element-553" data-mp="13g2qndbmhwvx">
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-554" data-mp="yjc5wcovid46">
                Contact Us
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-555" data-mp="18ff97lvgohsp">
              Let's start a{' '}
              <span className="text-brand-gold italic" data-id="element-556" data-mp="ko1gvfxb8m7q">conversation.</span>
            </h1>
            <p className="text-xl text-white/85 leading-relaxed font-light max-w-2xl" data-id="element-557" data-mp="fp716mun3dru">
              Whether you're exploring a new managed care partner, requesting a
              proposal, or just have a question — our team is ready to help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact methods */}
      <section className="py-12 bg-white" data-id="element-558" data-mp="2mf0mlwth0sl9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6" data-id="element-559" data-mp="2mg3di4qsmv16">
          {[{
          icon: Phone,
          title: 'Call us',
          lines: ['714.259.1053'],
          href: 'tel:7142591053'
        }, {
          icon: Mail,
          title: 'Email us',
          lines: ['info@arissacs.com', 'Response within one business day'],
          href: 'mailto:info@arissacs.com'
        }, {
          icon: Clock,
          title: 'Office hours',
          lines: ['Monday – Friday', '6:00 AM – 6:00 PM PST']
        }].map((card, i) => {
          const Icon = card.icon;
          const Wrapper: any = card.href ? 'a' : 'div';
          return <motion.div key={card.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} data-id="element-560" data-mp="255pxn83e5zz8" data-mpi={i}>
                <Wrapper {...card.href ? {
              href: card.href
            } : {}} className="block bg-brand-paper rounded-xl p-8 hover:shadow-lg transition-shadow border border-gray-100 h-full group" data-id="element-561" data-mp="2ixxnefw9cqof">
                  <div className="w-12 h-12 rounded-lg bg-brand-navy text-brand-gold flex items-center justify-center mb-5 group-hover:bg-brand-gold group-hover:text-brand-navy transition-colors" data-id="element-562" data-mp="13gdu25m7uo2z">
                    <Icon className="w-6 h-6" data-id="element-563" data-mp="3j7fc7t7x0vod" />
                  </div>
                  <h3 className="font-serif font-bold text-xl text-brand-navy mb-3" data-id="element-564" data-mp="3o4xkrii0t8vu">
                    {card.title}
                  </h3>
                  {card.lines.map(line => <p key={line} className="text-gray-700 text-sm leading-relaxed" data-id="element-565" data-mp="1r74xzhupcwf">
                      {line}
                    </p>)}
                </Wrapper>
              </motion.div>;
        })}
        </div>
      </section>

      {/* Form + Imagery panel */}
      <section className="py-24 bg-brand-paper" data-id="element-566" data-mp="fnt1n75zcvp9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-567" data-mp="2cjid34mfnoa0">
          <div className="grid lg:grid-cols-5 gap-10" data-id="element-568" data-mp="2hha5s0uwjef9">
            {/* Imagery & quote panel (left) */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="lg:col-span-2 relative rounded-2xl overflow-hidden min-h-[500px] lg:min-h-full bg-brand-navy" data-id="element-569" data-mp="3ks61zys8g4dv">
              <img src="https://cdn.magicpatterns.com/uploads/eVoYVX7dZpd3mczqXHJ3SW/Depositphotos_family_care.jpg" alt="A doctor smiling with a father and his young son during a care visit" className="absolute inset-0 w-full h-full object-cover" data-id="element-570" data-mp="2n2ts56yzc98" />
              <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/40 via-brand-navy/70 to-brand-navy/95" data-id="element-571" data-mp="qbvvyje6qgca"></div>

              {/* Content overlay */}
              <div className="relative z-10 p-8 md:p-12 h-full flex flex-col justify-end text-white" data-id="element-572" data-mp="lbhksshaeqa1">
                <Quote className="w-10 h-10 text-brand-gold mb-6" data-id="element-573" data-mp="14x9qii399fpz" />
                <p className="font-serif text-2xl md:text-3xl leading-snug mb-6 italic font-light" data-id="element-574" data-mp="19xp0r0s8q73q">
                  "Behind every claim is a person — and a family behind every
                  recovery."
                </p>
                <div className="border-t border-white/20 pt-6" data-id="element-575" data-mp="101udttgzhpv5">
                  <p className="text-sm text-white/80 leading-relaxed" data-id="element-576" data-mp="m7uqmqzb2wn6">
                    When you reach out to Arissa, you're not just starting a
                    vendor relationship. You're joining a team that treats every
                    injured worker like one of our own.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Form (right) */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="lg:col-span-3" data-id="element-577" data-mp="3aa9k62s92d4">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12" data-id="element-578" data-mp="durxqcy33f5z">
                {submitted ? <div className="text-center py-12" data-id="element-579" data-mp="4rqseeh1ls3d">
                    <div className="w-20 h-20 bg-brand-gold/15 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-580" data-mp="4tk2k3qv6ug4">
                      <CheckCircle2 className="w-10 h-10 text-brand-gold" data-id="element-581" data-mp="3w4tqs611w8zk" />
                    </div>
                    <h3 className="text-3xl font-serif text-brand-navy mb-3" data-id="element-582" data-mp="3r58rfuhyxymb">
                      Thanks — we'll be in touch
                    </h3>
                    <p className="text-gray-600 max-w-md mx-auto" data-id="element-583" data-mp="3h844hsgatz4g">
                      Your message is on its way. A member of our team will be
                      in touch within one business day.
                    </p>
                  </div> : <>
                    <h2 className="text-3xl font-serif text-brand-navy mb-2" data-id="element-584" data-mp="1wqbshrj087pg">
                      Send us a message
                    </h2>
                    <p className="text-gray-600 mb-8" data-id="element-585" data-mp="21odd4gw2wf31">
                      Tell us a little about what you're looking for and we'll
                      be in touch.
                    </p>

                    <form method="POST" action="https://formspree.io/f/xnjywlka" onSubmit={handleSubmit} className="space-y-5" data-id="element-586" data-mp="18zpkg0902nfg">
                      <input type="hidden" name="_subject" value="New Arissa CS contact form submission" data-id="element-587" data-mp="1nr6tyl7g0eda" />

                      <div data-id="element-588" data-mp="v9ue7a1jif5f">
                        <label className="block text-sm font-semibold text-brand-navy mb-2" data-id="element-589" data-mp="v92twvde83ug">
                          Full Name
                        </label>
                        <input required name="name" className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 focus:outline-none transition" data-id="element-590" data-mp="twf84mbr6tke" />
                      </div>

                      <div className="grid sm:grid-cols-2 gap-5" data-id="element-591" data-mp="68fixqu3bb7x">
                        <div data-id="element-592" data-mp="b5o0pqbbtd8n">
                          <label className="block text-sm font-semibold text-brand-navy mb-2" data-id="element-593" data-mp="b6z07dxqnyty">
                            Email
                          </label>
                          <input required type="email" name="email" className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 focus:outline-none transition" data-id="element-594" data-mp="kwinqwwecgv5" />
                        </div>
                        <div data-id="element-595" data-mp="21onjm58n73to">
                          <label className="block text-sm font-semibold text-brand-navy mb-2" data-id="element-596" data-mp="21pfgdjdz77lj">
                            Phone
                          </label>
                          <input type="tel" name="phone" className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 focus:outline-none transition" data-id="element-597" data-mp="1rqgfwo3d6i9m" />
                        </div>
                      </div>

                      <div data-id="element-598" data-mp="1dwfd9j9akb3z">
                        <label className="block text-sm font-semibold text-brand-navy mb-2" data-id="element-599" data-mp="1908hgjam0fzr">
                          Website
                        </label>
                        <input type="url" name="website" placeholder="https://" className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 focus:outline-none transition" data-id="element-600" data-mp="142q50ixdvin0" />
                      </div>

                      <div data-id="element-601" data-mp="gf1n7zs1bq1y">
                        <label className="block text-sm font-semibold text-brand-navy mb-2" data-id="element-602" data-mp="keu2evbxouga">
                          Message
                        </label>
                        <textarea required name="message" rows={5} placeholder="A few details about what you're looking for..." className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 focus:outline-none transition resize-none" data-id="element-603" data-mp="184y3auxqmg3l"></textarea>
                      </div>

                      <label className="flex items-start gap-3 pt-2 cursor-pointer group" data-id="element-604" data-mp="1vusez96blym9">
                        <input required type="checkbox" name="privacyAgreed" className="mt-0.5 w-4 h-4 rounded border-gray-300 text-brand-gold focus:ring-2 focus:ring-brand-gold/30 cursor-pointer flex-shrink-0" data-id="element-605" data-mp="3gj3829ndeddm" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-606" data-mp="3ldgw1qzpdi89">
                          By submitting this form, you agree to our{' '}
                          <Link to="/privacy" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-607" data-mp="2sniai5kpw38c">
                            privacy policy
                          </Link>
                          . We never share your information.
                        </span>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer group" data-id="element-608" data-mp="2niy04nsas1sj">
                        <input type="checkbox" name="smsConsent" className="mt-0.5 w-4 h-4 rounded border-gray-300 text-brand-gold focus:ring-2 focus:ring-brand-gold/30 cursor-pointer flex-shrink-0" data-id="element-609" data-mp="2shrfymv244vv" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-610" data-mp="36hk182ntdswg">
                          I agree to receive text messages (SMS) from Arissa
                          Cost Solutions at the phone number provided. Message
                          and data rates may apply; reply STOP to opt out.
                        </span>
                      </label>

                      {error && <p className="text-sm text-red-600" data-id="element-611" data-mp="3p8a802gi9zul">
                          Something went wrong. Please try again or call us
                          directly.
                        </p>}

                      <button type="submit" disabled={submitting} className="bg-brand-gold text-brand-navy px-8 py-4 rounded-lg font-semibold hover:bg-brand-navy hover:text-white transition-colors flex items-center justify-center w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-612" data-mp="86p5s3blrn39">
                        {submitting ? 'Sending…' : 'Send message'}{' '}
                        {!submitting && <Send className="w-4 h-4 ml-2" data-id="element-613" data-mp="lwk8wknrnf92" />}
                      </button>
                    </form>
                  </>}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HQ + Who We Work With with imagery */}
      <section className="py-24 bg-white" data-id="element-614" data-mp="19mejswoiytqo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start" data-id="element-615" data-mp="19ng6xzitf6or">
          {/* HQ card with image */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-brand-navy rounded-2xl overflow-hidden relative shadow-xl" data-id="element-616" data-mp="14u3ne3sztq2v">
            <div className="relative h-64" data-id="element-617" data-mp="2gcrbrtg9l8kh">
              <img src="https://cdn.magicpatterns.com/uploads/5WTAe3hurQwKJRcgBu9hXH/ArissaCS-Buiilding.png" alt="Arissa Cost Solutions corporate headquarters building" className="w-full h-full object-cover" data-id="element-618" data-mp="2gbze8d33x0rk" />
            </div>
            <div className="p-8 md:p-10 relative z-10" data-id="element-619" data-mp="2bepsp8iu9990">
              <h3 className="font-serif text-3xl text-white mb-6" data-id="element-620" data-mp="4n88gwrp8cob">
                Visit our headquarters
              </h3>
              <div className="space-y-5" data-id="element-621" data-mp="9lrvz1lxxwis">
                <div className="flex items-start" data-id="element-622" data-mp="9kgsl2weqqs7">
                  <MapPin className="w-5 h-5 text-brand-gold mr-3 mt-1 flex-shrink-0" data-id="element-623" data-mp="1u5wyw56z18mz" />
                  <div className="text-white/85 text-sm leading-relaxed" data-id="element-624" data-mp="1p6k2otu7mp7q">
                    Corporate Headquarters
                    <br data-id="element-625" data-mp="23vgkvjpzdim2" />
                    2415 Campus Dr., Suite 250
                    <br data-id="element-626" data-mp="1z3gcr3zrbjee" />
                    Irvine, CA 92612
                  </div>
                </div>
                <div className="flex items-start" data-id="element-627" data-mp="2culgah68wvu0">
                  <Phone className="w-5 h-5 text-brand-gold mr-3 mt-1 flex-shrink-0" data-id="element-628" data-mp="2cxg149t68iny" />
                  <div className="text-sm" data-id="element-629" data-mp="28ti3k3jhvfx0">
                    <a href="tel:7142591053" className="block text-white/90 hover:text-brand-gold transition-colors" data-id="element-630" data-mp="2mu2lkxhl57pg">
                      714.259.1053
                    </a>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-631" data-mp="ngf1pgzyu060">
                  <Mail className="w-5 h-5 text-brand-gold mr-3 mt-1 flex-shrink-0" data-id="element-632" data-mp="nhztwzyt570p" />
                  <a href="mailto:info@arissacs.com" className="text-sm text-white/90 hover:text-brand-gold transition-colors" data-id="element-633" data-mp="1m7w496os5b4">
                    info@arissacs.com
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Who we work with with imagery */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="bg-brand-paper rounded-2xl overflow-hidden relative shadow-sm border border-gray-100" data-id="element-634" data-mp="3eub07xenu4ra">
            <div className="relative w-full aspect-[3/2]" data-id="element-635" data-mp="190t7iq8xdugi">
              <img src="https://cdn.magicpatterns.com/uploads/hY2rQvJwQnbc4WDQPVKw7M/Depositphotos_workers.jpg" alt="Diverse group of workers and managers — a hard-hat worker, professionals, and an executive" className="absolute inset-0 w-full h-full object-cover object-top" data-id="element-636" data-mp="18z9rn0r6llf9" />
            </div>
            <div className="p-8 md:p-10 relative z-10" data-id="element-637" data-mp="2euhayg0vsqqg">
              <h3 className="font-serif text-3xl text-brand-navy mb-6" data-id="element-638" data-mp="2erv4yktn2jac">
                Who we work with
              </h3>
              <ul className="space-y-4" data-id="element-639" data-mp="25tato4qerbw5">
                <li className="flex items-start bg-white p-4 rounded-lg shadow-sm" data-id="element-640" data-mp="25s8543gk5lje">
                  <div className="w-10 h-10 rounded-lg bg-brand-navy text-brand-gold flex items-center justify-center mr-4 flex-shrink-0" data-id="element-641" data-mp="20teoi25z5ef0">
                    <Building2 className="w-5 h-5" data-id="element-642" data-mp="1vwxxn07e4ly8" />
                  </div>
                  <div data-id="element-643" data-mp="3v6werwxrkpdq">
                    <div className="font-semibold text-brand-navy" data-id="element-644" data-mp="3v7ftz5w14zo2">
                      Insurance Carriers
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-645" data-mp="lxjkx7zl26mg">
                      Comprehensive managed care for national & regional
                      carriers.
                    </div>
                  </div>
                </li>
                <li className="flex items-start bg-white p-4 rounded-lg shadow-sm" data-id="element-646" data-mp="c6yh9651ybwo">
                  <div className="w-10 h-10 rounded-lg bg-brand-navy text-brand-gold flex items-center justify-center mr-4 flex-shrink-0" data-id="element-647" data-mp="79or1o9u9w7s">
                    <Briefcase className="w-5 h-5" data-id="element-648" data-mp="38ms78ju3m6c" />
                  </div>
                  <div data-id="element-649" data-mp="3uctko9g02zr2">
                    <div className="font-semibold text-brand-navy" data-id="element-650" data-mp="3ucbhstyu1na6">
                      Third Party Administrators
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-651" data-mp="3kfymjrnn31db">
                      White-label and co-branded solutions for TPAs of every
                      size.
                    </div>
                  </div>
                </li>
                <li className="flex items-start bg-white p-4 rounded-lg shadow-sm" data-id="element-652" data-mp="1ipdd8df9qr7j">
                  <div className="w-10 h-10 rounded-lg bg-brand-navy text-brand-gold flex items-center justify-center mr-4 flex-shrink-0" data-id="element-653" data-mp="1r41dgpkmb2ac">
                    <Users className="w-5 h-5" data-id="element-654" data-mp="1v4l6bgy5rjqo" />
                  </div>
                  <div data-id="element-655" data-mp="1zypjihy0oo07">
                    <div className="font-semibold text-brand-navy" data-id="element-656" data-mp="1zzhbliseo3lq">
                      Public & Private Employers
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-657" data-mp="186qgjbmgkky2">
                      Self-insured employers across every major industry.
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}